/**
 * useSiteMeta — 현장 메타(표시명·위치) 실시간 구독.
 *
 * Map<`${scode}_${hcode}`, SiteMeta> 로 반환해 lookup O(1).
 * 옛 useSiteAliases 를 대체한다 (키가 현장명 문자열 → 현장 코드로 바뀌었다).
 */
import { useEffect, useMemo, useState } from "react";
import { siteMetaRepo } from "@/services/site_meta.repository";
import type { SiteMeta } from "@/domain/site_meta";

export interface UseSiteMetaState {
  /** `${scode}_${hcode}` → SiteMeta */
  metas: Map<string, SiteMeta>;
  rows: SiteMeta[];
  loading: boolean;
  error: string | null;
}

export function useSiteMeta(): UseSiteMetaState {
  const [rows, setRows] = useState<SiteMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsub = siteMetaRepo.subscribeAll(
      (data) => {
        setRows(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        // 권한/네트워크 에러 — 빈 목록으로 폴백. 화면은 규칙 표시명으로 계속 동작한다
        setRows([]);
        setLoading(false);
        setError(err.message);
      },
    );
    return unsub;
  }, []);

  const metas = useMemo(() => {
    const m = new Map<string, SiteMeta>();
    for (const r of rows) m.set(r.id, r);
    return m;
  }, [rows]);

  return { metas, rows, loading, error };
}
